import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, FileText, Clock, Download, DollarSign } from 'lucide-react';
import { SummaryCard } from './SummaryCard';
import { formatCurrency, formatMonth } from './utils';
import { exportToCSV } from '../../utils/csvExport';
import { getTodayString } from '../../utils/dateUtils';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;

export function ProfitReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('paid_pending');

  useEffect(() => { fetchReport(); }, []);

  const fetchReport = async () => {
    try { 
      const token = localStorage.getItem('token'); 
      const response = await axios.get(`${API}/profit-report`, { headers: { Authorization: `Bearer ${token}` } }); 
      setReport(response.data); 
    } catch (error) { 
      toast.error('Failed to load profit report'); 
    } finally { 
      setLoading(false); 
    }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-slate-300 border-t-amber-500 rounded-full animate-spin" /></div>;

  const grossProfitData = report?.gross_profit_data || {};
  const ytdData = report?.ytd_all;
  const monthlyData = filterType === 'paid_pending' ? report?.monthly_paid_pending : report?.monthly_paid;
  const showPending = filterType === 'paid';

  // Calculate monthly totals
  const monthlyTotals = monthlyData?.reduce((acc, r) => ({ 
    income: acc.income + r.income, 
    expenses: acc.expenses + r.expenses, 
    net_profit: acc.net_profit + r.net_profit,
    pending: acc.pending + (r.pending_amount || 0)
  }), { income: 0, expenses: 0, net_profit: 0, pending: 0 }) || { income: 0, expenses: 0, net_profit: 0, pending: 0 };

  // Calculate YTD display values based on filter type
  const displayIncome = filterType === 'paid' ? ytdData?.paid_income : ytdData?.income;
  const displayNetProfit = filterType === 'paid' 
    ? (ytdData?.paid_income - ytdData?.expenses) 
    : ytdData?.net_profit;

  const handleExportCSV = () => {
    if (!report || !monthlyData || monthlyData.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const columns = [
      { key: 'month', label: 'Month' },
      { key: 'income', label: 'Income (₹)' },
      { key: 'expenses', label: 'Expenses (₹)' },
      { key: 'net_profit', label: 'Net Profit (₹)' }
    ];
    
    if (filterType === 'paid') {
      columns.push({ key: 'pending_amount', label: 'Pending Amount (₹)' });
    }
    
    const formattedData = monthlyData.map(row => ({
      ...row,
      month: formatMonth(row.month)
    }));
    
    const filename = `profit_report_${filterType}_${getTodayString()}`;
    exportToCSV(formattedData, columns, filename);
    toast.success('Profit report exported to CSV');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Profit Report</h3>
          <p className="text-sm text-slate-500">Financial overview and performance metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            data-testid="export-profit-report-csv"
            variant="outline"
            onClick={handleExportCSV}
            disabled={!report}
            className="rounded-xl h-9 w-9 p-0"
            title="Export CSV"
          >
            <Download className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 bg-slate-50 rounded-xl">
        <div className="flex items-center gap-3">
          <Label className="text-sm font-medium whitespace-nowrap">Income Filter:</Label>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48 h-10 rounded-lg bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="paid_pending">Paid & Pending</SelectItem>
              <SelectItem value="paid">Paid Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-slate-500">
          {filterType === 'paid_pending' 
            ? 'Net Profit = (Paid Income + Pending Income) - Expenses'
            : 'Net Profit = Paid Income - Expenses (Pending shown separately)'}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard title="Income (Received)" amount={monthlyTotals.income} icon={TrendingUp} gradient="bg-gradient-to-br from-emerald-500 to-emerald-600" />
        <SummaryCard title="Expenses" amount={monthlyTotals.expenses} icon={TrendingDown} gradient="bg-gradient-to-br from-red-500 to-red-600" />
        <SummaryCard title="Gross Profit" amount={grossProfitData.gross_profit || 0} icon={DollarSign} gradient="bg-gradient-to-br from-purple-500 to-purple-600" />
        <SummaryCard title="Net Profit" amount={monthlyTotals.net_profit} icon={monthlyTotals.net_profit >= 0 ? TrendingUp : TrendingDown} gradient={monthlyTotals.net_profit >= 0 ? "bg-gradient-to-br from-slate-800 to-slate-900" : "bg-gradient-to-br from-red-600 to-red-700"} />
      </div>
      
      {/* Pending Amount Card (only for paid mode) */}
      {showPending && monthlyTotals.pending > 0 && (
        <Card className="p-4 border-0 rounded-2xl shadow-lg bg-gradient-to-br from-blue-500 to-blue-600 overflow-hidden">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg flex-shrink-0"><Clock className="w-5 h-5 text-white" /></div>
            <div className="min-w-0 flex-1">
              <span className="text-xs sm:text-sm text-white/80">Pending Amount to be Received</span>
              <p className="text-lg sm:text-2xl font-bold text-white truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>{formatCurrency(monthlyTotals.pending)}</p>
            </div>
          </div>
        </Card>
      )}
      
      {/* Gross Profit Breakdown */}
      <Card className="p-4 border-0 rounded-xl bg-purple-50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg"><DollarSign className="w-5 h-5 text-purple-600" /></div>
          <div className="flex-1">
            <span className="text-sm text-purple-700 font-medium">Gross Profit Breakdown</span>
            <div className="flex flex-wrap gap-4 mt-1 text-sm text-purple-600">
              <span>Silica Loads Sent: {formatCurrency(grossProfitData.total_loads_income || 0)}</span>
              <span>-</span>
              <span>Silica Purchased: {formatCurrency(grossProfitData.total_purchase_cost || 0)}</span>
              <span>=</span>
              <span className="font-bold">{formatCurrency(grossProfitData.gross_profit || 0)}</span>
            </div>
          </div>
        </div>
      </Card>
      
      {/* Monthly Table */}
      <div>
        <h4 className="text-md font-bold text-slate-800 mb-3">Monthly Breakdown</h4>
        {(!monthlyData || monthlyData.length === 0) ? (
          <Card className="p-12 text-center border-0 rounded-2xl shadow-lg bg-white">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No monthly data available</p>
          </Card>
        ) : (
          <Card className="border-0 rounded-2xl shadow-lg bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Month</th>
                    <th className="px-4 py-3 text-right font-semibold text-slate-700">Income</th>
                    <th className="px-4 py-3 text-right font-semibold text-slate-700">Expenses</th>
                    <th className="px-4 py-3 text-right font-semibold text-slate-700">Net Profit</th>
                    {showPending && <th className="px-4 py-3 text-right font-semibold text-slate-700">Pending</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {monthlyData.map((row, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900">{formatMonth(row.month)}</td>
                      <td className="px-4 py-3 text-right text-emerald-600">{formatCurrency(row.income)}</td>
                      <td className="px-4 py-3 text-right text-red-600">{formatCurrency(row.expenses)}</td>
                      <td className={`px-4 py-3 text-right font-bold ${row.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(row.net_profit)}</td>
                      {showPending && <td className="px-4 py-3 text-right text-blue-600">{formatCurrency(row.pending_amount || 0)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* YTD Summary Section */}
      {ytdData && (
        <div>
          <h4 className="text-md font-bold text-slate-800 mb-3">Year-to-Date (YTD) Summary</h4>
          <Card className="border-0 rounded-2xl shadow-lg bg-white p-6">
            <p className="text-sm text-slate-500 mb-4">Period: {ytdData.start_date} to {ytdData.end_date}</p>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-600">Received Income (Paid)</span>
                <span className="font-semibold text-emerald-600">{formatCurrency(ytdData.paid_income)}</span>
              </div>
              {filterType === 'paid_pending' && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-slate-600">Total Income (Paid + Pending)</span>
                  <span className="font-semibold text-blue-600">{formatCurrency(ytdData.income)}</span>
                </div>
              )}
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-600">Pending Income</span>
                <span className={`font-semibold ${filterType === 'paid' ? 'text-blue-600' : 'text-amber-600'}`}>
                  {formatCurrency(ytdData.pending_income)}
                  {filterType === 'paid' && <span className="text-xs ml-2">(Not in Net Profit)</span>}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-600">Total Expenses</span>
                <span className="font-semibold text-red-600">{formatCurrency(ytdData.expenses)}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-slate-600">Gross Profit (Loads Sent - Silica Purchased)</span>
                <span className="font-semibold text-purple-600">{formatCurrency(grossProfitData.gross_profit || 0)}</span>
              </div>
              <div className="flex justify-between py-3 bg-slate-50 px-3 rounded-lg">
                <span className="font-bold text-slate-900">
                  Net Profit ({filterType === 'paid' ? 'Paid Income' : 'Total Income'} - Expenses)
                </span>
                <span className={`font-bold text-lg ${displayNetProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(displayNetProfit)}
                </span>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
